import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useP } from '../../i18n/useP';
import CardShell from '../../components/CardShell';
import { Cell, Choix } from '../../components/InlineCells';
import Listbox from '../../components/Listbox';
import { getBsView } from '../../api/financials_bs';
import { listLines } from '../../api/financials_pl';
import { getForecastAssumptions, putForecastAssumption } from '../../api/forecastAssumptions';
import { accountOptions } from './accountNature';
import './CashMoves.css';

/* ÉCRITURES SANS IMPACT SUR LA LIQUIDITÉ (décision Olivier, 2026-10-20) : la
   seconde carte des Opérations spéciales. Une ligne = un libellé, un compte au
   DÉBIT, un compte au CRÉDIT et des francs par mois ouvert, comme une écriture
   de journal : n'importe qui la saisit comme le ferait un comptable. Le plan de
   liquidité ne la voit pas. Sans le module Finance elle se lit grisée, ses
   comptes n'existant pas.
   N'IMPORTE QUEL COMPTE, du résultat ou du bilan, sauf la liquidité (première
   carte) et les deux résultats du bilan ; une recherche par le nom retrouve le
   compte dans la liste. Libérer une provision de personnel se passe au crédit
   de sa ligne, au débit du passif. */

const MONTHS_ABBR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const BS_CLOSED = ['liquidity', 'current_result', 'retained_earnings'];

export default function SpecialEntriesCard({ activeTenant, year, unit = 'kchf', canWrite = true }) {
  const p = useP();
  const tid = activeTenant?.id;
  const hasFin = (activeTenant?.enabled_modules || []).includes('financials');
  const [closed, setClosed] = useState(null);
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const div = unit === 'chf' ? 1 : 1000;

  const load = useCallback(() => {
    if (!tid) { setClosed(null); setEntries([]); return; }
    Promise.all([
      getBsView(tid, year, { flatForecast: true }).catch(() => null),
      listLines(tid).catch(() => null),
    ]).then(([rb, rp]) => {
      const bsv = rb && !rb.error ? rb.data : null;
      setClosed(Number(bsv?.closed_count) || 0);
      const pl = (Array.isArray(rp?.data) ? rp.data : []).filter((l) => !l.hidden);
      const bs = (bsv?.lines || []).filter((l) => !BS_CLOSED.includes(l.std_key));
      setAccounts(accountOptions(pl, bs, p));
    });
    getForecastAssumptions(tid, year).then((r) => {
      const v = r.error ? null : r.data?.special_entries;
      setEntries(Array.isArray(v) ? v.filter((e) => e && e.id) : []);
    }).catch(() => setEntries([]));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  const months = useMemo(() => {
    const out = [];
    for (let m = (closed || 0) + 1; m <= 12; m += 1) out.push(m);
    return out;
  }, [closed]);

  if (!tid || closed == null) return null;

  const save = (next) => {
    setEntries(next);
    putForecastAssumption(tid, year, 'special_entries', next).catch(() => {});
  };
  const write = canWrite && hasFin;
  const add = () => save([...entries, { id: `se_${Math.random().toString(36).slice(2, 9)}`, label: '', debit: null, credit: null, months: {} }]);
  const patch = (id, fn) => save(entries.map((e) => (e.id === id ? fn(e) : e)));
  const del = (id) => save(entries.filter((e) => e.id !== id));
  const fmtU = (chf) => (chf == null || Math.abs(Number(chf)) < 0.5 ? '' : Math.round(Number(chf) / div).toLocaleString('de-CH'));
  const money = (chf) => ({
    value: chf == null ? null : Math.round(Number(chf) / div), type: 'number',
    format: (x) => (x == null || Number(x) === 0 ? '' : Number(x).toLocaleString('de-CH')),
    toDraft: (x) => (x == null || Number(x) === 0 ? '' : String(x)),
  });
  const labelOf = (id) => accounts.find((o) => o.value === id)?.label;
  const picker = (value, onSave) => (write ? (
    <Listbox className="cmv-lb" value={value || ''} onChange={onSave} options={accounts} searchable
             placeholder={p('choisir un compte')} searchPlaceholder={p('Rechercher un compte…')} />
  ) : (
    <Choix value={value || ''} canWrite={false} options={[{ value: value || '', label: labelOf(value) || '' }]}
           emptyLabel="—" onSave={() => {}} />
  ));
  const sumOf = (e) => months.reduce((s, m) => s + (Number(e.months?.[String(m)]) || 0), 0);
  const total = {};
  months.forEach((m) => { total[m] = entries.reduce((s, e) => s + (Number(e.months?.[String(m)]) || 0), 0); });

  return (
    <div className={hasFin ? undefined : 'cmv-off'} title={hasFin ? undefined : p('Demande le module Finance')}>
      <CardShell
        className="report-card cmv-card"
        title={p('Opération sans impact sur la liquidité')}
        primaryAction={write ? <button type="button" className="lk-btn" onClick={add}>＋ {p('Ligne')}</button> : null}
        flush
      >
        <div className="lk-hscroll">
          <table className="lk-entry-table cmv-tbl">
            <colgroup>
              <col style={{ width: 220 }} />
              <col style={{ width: 220 }} />
              <col style={{ width: 220 }} />
              {months.map((m) => <col key={m} style={{ width: 76 }} />)}
              <col style={{ width: 90 }} />
              <col style={{ width: 30 }} />
            </colgroup>
            <thead>
              <tr>
                <th>{p('Libellé')}</th>
                <th>{p('Débit')}</th>
                <th>{p('Crédit')}</th>
                {months.map((m) => <th key={m} className="lk-num">{MONTHS_ABBR[m - 1]}</th>)}
                <th className="lk-num">{p('Total')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {entries.map((e) => (
                <tr key={e.id}>
                  <td>
                    <Cell value={e.label || ''} canWrite={write} placeholder={p('libellé')}
                          onSave={(v) => patch(e.id, (x) => ({ ...x, label: v }))} />
                  </td>
                  <td>
                    {picker(e.debit, (v) => patch(e.id, (x) => ({ ...x, debit: v || null })))}
                  </td>
                  <td>
                    {picker(e.credit, (v) => patch(e.id, (x) => ({ ...x, credit: v || null })))}
                  </td>
                  {months.map((m) => (
                    <td key={m} className="lk-num">
                      <Cell {...money(e.months?.[String(m)])} canWrite={write} placeholder="0"
                            onSave={(v) => patch(e.id, (x) => {
                              const next = { ...(x.months || {}) };
                              if (v == null || Number(v) === 0) delete next[String(m)];
                              else next[String(m)] = Math.abs(v) * div;
                              return { ...x, months: next };
                            })} />
                    </td>
                  ))}
                  <td className="lk-num">{fmtU(sumOf(e))}</td>
                  <td>
                    {write && (
                      <button type="button" className="lk-x lk-del" aria-label={p('Supprimer')} onClick={() => del(e.id)}>✕</button>
                    )}
                  </td>
                </tr>
              ))}
              {entries.length > 0 && (
                <tr className="lk-hi-strong">
                  <td>{p('Total')}</td>
                  <td />
                  <td />
                  {months.map((m) => <td key={m} className="lk-num">{fmtU(total[m])}</td>)}
                  <td className="lk-num">{fmtU(months.reduce((s, m) => s + total[m], 0))}</td>
                  <td />
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </CardShell>
    </div>
  );
}
